/**
 * HX Cabo Verde — Hub Energético Insular
 * Digital twin dashboard for the Cabo Verde archipelagic energy ecosystem.
 * Sister page of BioPintoSTP, same visual language, distinct focus (Energy vs Agriculture).
 *
 * Design tokens:
 *   --hn-neon: #0A9B5F (sage LED green, brand primary / production)
 *   --hn-cyan: #06B6D4 (grid / digital)
 *   --hn-solar:#F59E0B (solar / energy)
 */
import React from "react";
import { motion } from "framer-motion";
import {
  Activity, Sun, Wind, Battery, Zap, Radio, ArrowRight,
  CalendarRange, GraduationCap, Waves, Cpu, Building2, Ship,
} from "lucide-react";
import { Navbar } from "@/components/hx/Navbar";
import { Footer } from "@/components/hx/Footer";
import { CountryFlag } from "@/components/hanno/Flags";
import { useHannoContent } from "@/data/hanno-content";

const NEON = "#0A9B5F";
const CYAN = "#06B6D4";
const SOLAR = "#F59E0B";

// ============================================================================
//  Count-up hook (shared pattern; local copy to keep page self-contained)
// ============================================================================
function useCountUp(target, { duration = 1600, decimals = 0, suffix = "" } = {}) {
  const [value, setValue] = React.useState(0);
  React.useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  const formatted = decimals ? value.toFixed(decimals) : Math.round(value).toLocaleString("pt-PT");
  return `${formatted}${suffix}`;
}

const Metric = ({ icon: Icon, label, value, unit = "", accent = NEON, decimals = 0, sub, testid }) => {
  const num = useCountUp(value, { decimals, suffix: unit });
  return (
    <div
      data-testid={testid}
      className="hx-glass rounded-xl px-5 py-5 border transition-all hover:-translate-y-0.5"
      style={{ borderColor: `${accent}22`, boxShadow: `0 0 0 1px ${accent}0F inset` }}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.24em] text-hx-muted">{label}</div>
        <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: `${accent}12` }}>
          <Icon size={13} style={{ color: accent }} strokeWidth={1.6} />
        </div>
      </div>
      <div className="mt-3 font-display font-bold text-3xl lg:text-4xl tabular-nums tracking-tight" style={{ color: accent }}>
        {num}
      </div>
      {sub && <div className="mt-1 text-[11px] text-hx-muted/80">{sub}</div>}
    </div>
  );
};

const SectorCard = ({ icon: Icon, title, status, statusColor = NEON, kpiLabel, kpiValue, kpiSuffix = "%", accent = NEON, delay = 0, testid, image }) => {
  const pct = useCountUp(kpiValue, { duration: 1800 });
  const barPct = typeof kpiValue === "number" ? Math.min(100, kpiValue) : 0;
  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6, delay }}
      data-testid={testid}
      className="hx-glass rounded-2xl overflow-hidden border relative group"
      style={{ borderColor: `${accent}22` }}
    >
      {image && (
        <div className="relative aspect-[16/10] overflow-hidden">
          <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,15,26,0.90) 0%, rgba(10,15,26,0.90) 100%)" }} />
        </div>
      )}
      <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
           style={{ background: `radial-gradient(circle at 30% 0%, ${accent}0f 0%, transparent 60%)` }} />
      <div className="relative p-6 lg:p-7">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: `${accent}14`, border: `1px solid ${accent}30` }}>
            <Icon size={18} style={{ color: accent }} strokeWidth={1.6} />
          </div>
          <div className="font-display font-semibold text-lg tracking-tight">{title}</div>
        </div>
        <div className="mt-5 flex items-center gap-2">
          <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: statusColor }} />
          <span className="text-[11px] uppercase tracking-[0.22em] text-hx-muted">{status}</span>
        </div>
        <div className="mt-5">
          <div className="flex items-end justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-hx-muted">{kpiLabel}</div>
            <div className="font-display font-bold text-2xl tabular-nums" style={{ color: accent }}>{pct}{kpiSuffix}</div>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: `${barPct}%` }}
              viewport={{ once: true }}
              transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1], delay: delay + 0.2 }}
              className="h-full rounded-full"
              style={{ background: `linear-gradient(90deg, ${accent} 0%, ${accent}80 100%)`, boxShadow: `0 0 12px ${accent}80` }}
            />
          </div>
        </div>
      </div>
    </motion.article>
  );
};

// ============================================================================
//  Data blocks — CV-specific
// ============================================================================
const AI_ALERTS = [
  {
    time: "há 3 min",
    severity: "solar",
    icon: Sun,
    title: "Pico de produção solar detetado em Santiago",
    action: "BESS iniciou carga acelerada (94 kW): reserva para pico noturno na ilha da Boa Vista.",
  },
  {
    time: "há 9 min",
    severity: "grid",
    icon: Radio,
    title: "Rebalanceamento inter-ilhas · Santiago → Fogo",
    action: "HN AI redirecionou 128 kWh via cabo submarino para estabilizar frequência a 50 Hz.",
  },
  {
    time: "há 31 min",
    severity: "wind",
    icon: Wind,
    title: "Vento sustentado > 9 m/s em Sal",
    action: "Turbinas Vestas em plena capacidade (2,4 MW); redução de queima diesel na central térmica confirmada.",
  },
];

const FORECAST = [
  { window: "Hoje 18:00", event: "Pico de consumo · Praia",       forecast: "24.6 MW",  action: "BESS pré-carregado",       confidence: 97 },
  { window: "Amanhã 12:00", event: "Excedente solar · Santiago",    forecast: "+18%",     action: "Exportar via smart grid",   confidence: 92 },
  { window: "Semana 25", event: "Manutenção preventiva · Sal WT-04", forecast: "6 h",      action: "Janela às 03:00 (baixo vento)", confidence: 89 },
  { window: "Semana 26", event: "Reforço BESS · Boavista",           forecast: "+2 MWh",   action: "Contentor 07 chega dia 12", confidence: 99 },
];

const CLIENT_ECOSYSTEM = [
  { group: "ELECTRA · Utility Nacional",    consume: "Energia + Smart Grid",       pct: 42, icon: Building2 },
  { group: "Hotelaria Insular",             consume: "Solar + BESS + EV Fleet",    pct: 22, icon: Ship },
  { group: "Municípios Ilhas Rurais",       consume: "Microgrids + Formação",      pct: 18, icon: Waves },
  { group: "Escolas Técnicas & HX Academy", consume: "Currículo + Talent Pool",    pct: 18, icon: GraduationCap },
];

// ============================================================================
//  MAIN PAGE
// ============================================================================
export default function CaboVerdeHubPage() {
  const c = useHannoContent();
  const t = c.dashboards.cv;
  const cc = c.dashboards.common;
  return (
    <div className="App relative bg-hx-bg text-hx-text" data-testid="page-cv-hub">
      <Navbar />

      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute inset-0 hx-grid-bg opacity-[0.16]" />
        <div className="absolute top-0 left-1/4 w-[520px] h-[520px] rounded-full blur-3xl opacity-[0.10]" style={{ background: `radial-gradient(circle, ${SOLAR} 0%, transparent 70%)` }} />
        <div className="absolute top-1/3 right-0 w-[480px] h-[480px] rounded-full blur-3xl opacity-[0.09]" style={{ background: `radial-gradient(circle, ${CYAN} 0%, transparent 70%)` }} />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full blur-3xl opacity-[0.08]" style={{ background: `radial-gradient(circle, ${NEON} 0%, transparent 70%)` }} />
      </div>

      <main className="relative">
        {/* ==================================================================== */}
        {/*  1. HERO                                                              */}
        {/* ==================================================================== */}
        <section className="relative pt-32 lg:pt-40 pb-14 lg:pb-20 overflow-hidden" data-testid="cv-hero">
          <div className="absolute inset-0 -z-10">
            <img
              src="/hanno/cabo-verde/cv-hero.png"
              alt="Vista aérea do hub energético insular HX em Santiago, Cabo Verde"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,15,26,0.90) 0%, rgba(10,15,26,0.90) 45%, rgba(10,15,26,0.90) 100%)" }} />
            <div className="absolute inset-0" style={{ background: `radial-gradient(circle at 70% 30%, ${SOLAR}12 0%, transparent 55%)` }} />
          </div>

          <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border" style={{ background: `${NEON}0f`, borderColor: `${NEON}44` }} data-testid="cv-status-badge">
                  <span className="relative flex items-center justify-center w-2 h-2">
                    <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ background: NEON }} />
                    <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: NEON }} />
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: NEON }}>
                    {t.status_badge}
                  </span>
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border" style={{ background: `${CYAN}0d`, borderColor: `${CYAN}33` }}>
                  <CountryFlag slug="cabo-verde" className="w-5 h-3.5 rounded shadow" />
                  <span className="text-[10px] uppercase tracking-[0.24em] font-mono font-semibold" style={{ color: CYAN }}>
                    {t.country_label}
                  </span>
                </div>
              </div>

              <h1 className="mt-6 font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                {t.title_a}{" "}
                <span style={{ color: NEON }}>—</span>{" "}
                <span className="block sm:inline">{t.title_b}</span>
              </h1>
              <p className="mt-5 max-w-3xl text-base lg:text-lg text-hx-muted leading-relaxed">
                {t.lede}
              </p>

              <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Metric icon={Sun}     label={t.kpi_solar}  value={12.4} unit=" MWp" accent={SOLAR} decimals={1} sub={t.kpi_solar_sub}  testid="metric-solar-mwp" />
                <Metric icon={Battery} label={t.kpi_bess}   value={8}    unit=" MWh" accent={NEON}  sub={t.kpi_bess_sub}                testid="metric-bess" />
                <Metric icon={Wind}    label={t.kpi_wind}   value={2.4}  unit=" MW"  accent={CYAN}  decimals={1} sub={t.kpi_wind_sub}   testid="metric-wind" />
                <Metric icon={Zap}     label={t.kpi_diesel} value={38}   unit="%"    accent={SOLAR} sub={t.kpi_diesel_sub}              testid="metric-diesel" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* ==================================================================== */}
        {/*  2. LOOP ENERGÉTICO — Solar → BESS → Smart Grid Inter-Ilhas         */}
        {/* ==================================================================== */}
        <section className="relative py-14 lg:py-20 border-t border-white/[0.10]" data-testid="cv-loop">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <SectionHeader eyebrow={t.loop_eyebrow} title={t.loop_title} accent={SOLAR} />

            <div className="mt-10 grid lg:grid-cols-2 gap-6 lg:gap-8">
              <div className="relative rounded-2xl overflow-hidden min-h-[380px] lg:min-h-[440px] border group" style={{ borderColor: `${SOLAR}30` }} data-testid="cv-loop-image">
                <img src="/hanno/cabo-verde/cv-solar-bess-loop.png" alt={t.loop_overlay_title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,15,26,0.90) 0%, rgba(10,15,26,0.90) 100%)" }} />
                <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(circle at 20% 20%, ${SOLAR}18 0%, transparent 55%), radial-gradient(circle at 80% 80%, ${NEON}12 0%, transparent 55%)` }} />
                <div className="absolute bottom-5 left-5 right-5">
                  <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border backdrop-blur-xl" style={{ background: "rgba(10,15,26,0.90)", borderColor: `${SOLAR}44` }}>
                    <Activity size={11} style={{ color: SOLAR }} strokeWidth={1.7} />
                    <span className="text-[9px] uppercase tracking-[0.24em] font-mono font-semibold" style={{ color: SOLAR }}>
                      {t.loop_badge}
                    </span>
                  </div>
                  <div className="mt-2 font-display font-semibold text-lg text-white" style={{ textShadow: "0 1px 2px rgba(10,15,26,0.05)" }}>{t.loop_overlay_title}</div>
                  <div className="mt-0.5 text-xs text-white/70" style={{ textShadow: "0 1px 2px rgba(10,15,26,0.05)" }}>{t.loop_overlay_sub}</div>
                </div>
              </div>

              <div className="grid gap-5">
                <TelemetryCard
                  accent={SOLAR} icon={Sun} brand={t.tel_solar_brand} title={t.tel_solar_title}
                  rows={[
                    [t.tel_solar_r1, <SpanCount key="g" value={9.2} decimals={1} suffix=" MW" accent={SOLAR} />],
                    [t.tel_solar_r2, <SpanCount key="a" value={54} suffix=" MWh" accent={SOLAR} />],
                    [t.tel_solar_r3, <SpanCount key="x" value={12} suffix=" MWh" accent={SOLAR} />],
                  ]}
                  testid="cv-tel-solar"
                />
                <TelemetryCard
                  accent={NEON} icon={Battery} brand={t.tel_bess_brand} title={t.tel_bess_title}
                  rows={[
                    [t.tel_bess_r1, <SpanCount key="s" value={72} suffix=" %" accent={NEON} />],
                    [t.tel_bess_r2, <SpanCount key="c" value={38} accent={NEON} />],
                    [t.tel_bess_r3, <span key="r" className="font-mono font-semibold" style={{ color: NEON }}>{t.tel_bess_r3_val}</span>],
                  ]}
                  testid="cv-tel-bess"
                />
                <TelemetryCard
                  accent={CYAN} icon={Radio} brand={t.tel_grid_brand} title={t.tel_grid_title}
                  rows={[
                    [t.tel_grid_r1, <span key="i" className="font-mono font-semibold" style={{ color: CYAN }}>{t.tel_grid_r1_val}</span>],
                    [t.tel_grid_r2, <SpanCount key="l" value={240} suffix=" ms" accent={CYAN} />],
                    [t.tel_grid_r3, <SpanCount key="u" value={99.94} decimals={2} suffix=" %" accent={CYAN} />],
                  ]}
                  testid="cv-tel-grid"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ==================================================================== */}
        {/*  3. MATRIZ IOT — 4 sectors                                            */}
        {/* ==================================================================== */}
        <section className="relative py-14 lg:py-20 border-t border-white/[0.10]" data-testid="cv-matrix">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <SectionHeader eyebrow={t.matrix_eyebrow} title={t.matrix_title} accent={NEON} />

            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <SectorCard icon={Sun}     title={t.sec_solar} status={t.sec_solar_status} statusColor={SOLAR} kpiLabel={t.sec_solar_kpi} kpiValue={22} accent={SOLAR} delay={0}    testid="cv-sector-solar"  image="/hanno/cabo-verde/cv-sector-solar.png" />
              <SectorCard icon={Battery} title={t.sec_bess}  status={t.sec_bess_status}  statusColor={NEON}  kpiLabel={t.sec_bess_kpi}  kpiValue={99} accent={NEON}  delay={0.1}  testid="cv-sector-bess"   image="/hanno/cabo-verde/cv-sector-bess.png" />
              <SectorCard icon={Zap}     title={t.sec_ev}    status={t.sec_ev_status}    statusColor={CYAN}  kpiLabel={t.sec_ev_kpi}    kpiValue={64} accent={CYAN}  delay={0.2}  testid="cv-sector-ev"     image="/hanno/cabo-verde/cv-sector-ev.png" />
              <SectorCard icon={Cpu}     title={t.sec_grid}  status={t.sec_grid_status}  statusColor={NEON}  kpiLabel={t.sec_grid_kpi}  kpiValue={100} accent={NEON} delay={0.3} testid="cv-sector-grid"  image="/hanno/cabo-verde/cv-sector-grid.png" />
            </div>
          </div>
        </section>

        {/* ==================================================================== */}
        {/*  4. ALERTAS + FORECAST                                                */}
        {/* ==================================================================== */}
        <section className="relative py-14 lg:py-20 border-t border-white/[0.10]" data-testid="cv-forecast">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <SectionHeader eyebrow={t.forecast_eyebrow} title={t.forecast_title} accent={CYAN} />

            <div className="mt-10 grid lg:grid-cols-[1fr_1.3fr] gap-6 lg:gap-8">
              <div className="hx-glass rounded-2xl p-6 lg:p-7 border" style={{ borderColor: `${CYAN}22` }} data-testid="cv-alerts">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: `${CYAN}14`, border: `1px solid ${CYAN}30` }}>
                    <Radio size={16} style={{ color: CYAN }} strokeWidth={1.6} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-hx-muted">{cc.recent_alerts}</div>
                    <div className="font-display font-semibold text-lg tracking-tight">{cc.live_feed}</div>
                  </div>
                </div>
                <ul className="space-y-4">
                  {t.alerts.map((a, i) => {
                    const src = AI_ALERTS[i] || {};
                    return (
                    <motion.li key={i} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className="flex gap-3 pb-4 border-b border-white/[0.10] last:border-0 last:pb-0">
                      <div className="shrink-0 w-8 h-8 rounded-md flex items-center justify-center" style={{ background: severityBg(src.severity), border: `1px solid ${severityBorder(src.severity)}` }}>
                        {src.icon && <src.icon size={13} style={{ color: severityColor(src.severity) }} strokeWidth={1.6} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-sm font-semibold text-hx-text">{a.title}</div>
                          <span className="text-[9px] uppercase tracking-[0.2em] text-hx-muted/70 font-mono">{a.time}</span>
                        </div>
                        <div className="mt-1.5 text-xs text-hx-muted leading-relaxed">{a.action}</div>
                      </div>
                    </motion.li>
                    );
                  })}
                </ul>
              </div>

              <div className="hx-glass rounded-2xl p-6 lg:p-7 border" style={{ borderColor: `${NEON}22` }} data-testid="cv-forecast-panel">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: `${NEON}14`, border: `1px solid ${NEON}30` }}>
                    <CalendarRange size={16} style={{ color: NEON }} strokeWidth={1.6} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-hx-muted">{t.forecast_head}</div>
                    <div className="font-display font-semibold text-lg tracking-tight">{cc.ai_recommended}</div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-[0.22em] text-hx-muted border-b border-white/[0.10]">
                        <th className="text-left font-medium py-2 pr-3">{t.col_window}</th>
                        <th className="text-left font-medium py-2 pr-3">{t.col_event}</th>
                        <th className="text-left font-medium py-2 pr-3 hidden sm:table-cell">{t.col_forecast}</th>
                        <th className="text-left font-medium py-2 pr-3 hidden md:table-cell">{t.col_action}</th>
                        <th className="text-right font-medium py-2">{t.col_confidence}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {t.forecast.map((row, i) => {
                        const conf = FORECAST[i]?.confidence ?? 95;
                        return (
                        <motion.tr key={i} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} className="border-b border-white/[0.10] last:border-0">
                          <td className="py-3 pr-3 font-mono text-xs text-hx-muted">{row.window}</td>
                          <td className="py-3 pr-3 text-hx-text">{row.event}</td>
                          <td className="py-3 pr-3 hidden sm:table-cell text-hx-muted">{row.forecast}</td>
                          <td className="py-3 pr-3 hidden md:table-cell text-hx-muted">{row.action}</td>
                          <td className="py-3 text-right">
                            <span className="inline-flex items-center gap-1.5 text-xs font-mono font-semibold px-2 py-0.5 rounded" style={{ background: `${NEON}12`, color: NEON, border: `1px solid ${NEON}30` }}>
                              {conf}%
                            </span>
                          </td>
                        </motion.tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ==================================================================== */}
        {/*  5. HX ACADEMY + ECOSSISTEMA                                          */}
        {/* ==================================================================== */}
        <section className="relative py-14 lg:py-20 border-t border-white/[0.10]" data-testid="cv-impact">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <SectionHeader eyebrow={t.impact_eyebrow} title={t.impact_title} accent={SOLAR} />

            <div className="mt-10 grid lg:grid-cols-[1.15fr_1fr] gap-6 lg:gap-8">
              <div className="hx-glass rounded-2xl p-6 lg:p-8 border" style={{ borderColor: `${SOLAR}22` }} data-testid="cv-academy">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: `${SOLAR}14`, border: `1px solid ${SOLAR}30` }}>
                    <GraduationCap size={18} style={{ color: SOLAR }} strokeWidth={1.6} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-hx-muted">{t.academy_eyebrow}</div>
                    <div className="font-display font-semibold text-xl tracking-tight">{t.academy_title}</div>
                  </div>
                </div>
                <p className="text-sm text-hx-muted leading-relaxed">{t.academy_lede}</p>
                <div className="mt-6 relative rounded-xl overflow-hidden aspect-[16/9] border group" style={{ borderColor: `${SOLAR}30` }}>
                  <img src="/hanno/people/team-cabo-verde-control.png" alt={t.academy_photo_caption} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,15,26,0.90) 0%, rgba(10,15,26,0.90) 100%)" }} />
                  <div className="absolute bottom-4 left-5 right-5">
                    <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border backdrop-blur-xl" style={{ background: "rgba(10,15,26,0.90)", borderColor: `${SOLAR}44` }}>
                      <GraduationCap size={11} style={{ color: SOLAR }} strokeWidth={1.7} />
                      <span className="text-[9px] uppercase tracking-[0.24em] font-mono font-semibold" style={{ color: SOLAR }}>
                        {t.academy_photo_tag}
                      </span>
                    </div>
                    <div className="mt-2 text-sm font-semibold text-white" style={{ textShadow: "0 1px 2px rgba(10,15,26,0.05)" }}>
                      {t.academy_photo_caption}
                    </div>
                  </div>
                </div>
                <div className="mt-6 grid grid-cols-3 gap-3">
                  <MiniStat label={t.mini_students} value={183} accent={SOLAR} />
                  <MiniStat label={t.mini_classes}  value={7}   accent={NEON} />
                  <MiniStat label={t.mini_jobs}     value={62}  accent={CYAN} />
                </div>
              </div>

              <div className="hx-glass rounded-2xl p-6 lg:p-7 border" style={{ borderColor: `${NEON}22` }} data-testid="cv-ecosystem">
                <div className="text-[10px] uppercase tracking-[0.24em] text-hx-muted">{cc.client_ecosystem}</div>
                <div className="mt-1 font-display font-semibold text-xl tracking-tight">{cc.who_consumes}</div>
                <ul className="mt-6 space-y-4">
                  {t.clients.map((cli, i) => {
                    const src = CLIENT_ECOSYSTEM[i] || {};
                    return <ClientRow key={cli.group} row={{ ...cli, pct: src.pct, icon: src.icon }} delay={i * 0.1} />;
                  })}
                </ul>
              </div>
            </div>

            <div className="mt-14 hx-glass-strong rounded-2xl p-8 lg:p-10 border text-center" style={{ borderColor: `${NEON}30` }}>
              <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: NEON }}>{cc.replicability}</div>
              <h3 className="mt-3 font-display font-bold text-2xl lg:text-3xl tracking-tight">
                {t.cta_title}
              </h3>
              <p className="mt-3 text-sm text-hx-muted max-w-2xl mx-auto">
                {t.cta_lede}
              </p>
              <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
                <a href="/contact" className="btn-hx-primary inline-flex items-center gap-2" data-testid="cv-cta-partner">
                  {cc.request_meeting} <ArrowRight size={14} />
                </a>
                <a href="/projects" className="text-[11px] uppercase tracking-[0.24em] text-hx-muted hover:text-hx-neon transition-colors inline-flex items-center gap-2" data-testid="cv-cta-projects">
                  {cc.other_projects} <ArrowRight size={12} />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

// ============================================================================
//  Helpers (co-located)
// ============================================================================
const SectionHeader = ({ eyebrow, title, accent }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: accent }}>{eyebrow}</div>
    <h2 className="mt-3 font-display font-bold text-3xl lg:text-4xl tracking-tight max-w-3xl leading-tight">{title}</h2>
  </div>
);

const TelemetryCard = ({ accent, icon: Icon, brand, title, rows, testid }) => (
  <div className="hx-glass rounded-xl p-5 lg:p-6 border" style={{ borderColor: `${accent}22` }} data-testid={testid}>
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: `${accent}14`, border: `1px solid ${accent}30` }}>
        <Icon size={16} style={{ color: accent }} strokeWidth={1.6} />
      </div>
      <div>
        <div className="text-[9px] uppercase tracking-[0.24em] text-hx-muted">{brand}</div>
        <div className="font-display font-semibold text-base tracking-tight">{title}</div>
      </div>
    </div>
    <dl className="mt-4 space-y-2.5">
      {rows.map(([k, v], i) => (
        <div key={i} className="flex items-baseline justify-between gap-4 border-b border-white/[0.10] last:border-0 pb-2 last:pb-0">
          <dt className="text-xs text-hx-muted">{k}</dt>
          <dd className="text-sm text-hx-text">{v}</dd>
        </div>
      ))}
    </dl>
  </div>
);

const SpanCount = ({ value, suffix, accent, decimals = 0 }) => {
  const num = useCountUp(value, { suffix, decimals });
  return <span className="font-mono font-semibold tabular-nums" style={{ color: accent }}>{num}</span>;
};

const MiniStat = ({ label, value, accent }) => {
  const num = useCountUp(value);
  return (
    <div className="rounded-lg px-3 py-3 border" style={{ borderColor: `${accent}22`, background: `${accent}06` }}>
      <div className="text-[9px] uppercase tracking-[0.22em] text-hx-muted">{label}</div>
      <div className="mt-1 font-display font-bold text-xl tabular-nums" style={{ color: accent }}>{num}</div>
    </div>
  );
};

const ClientRow = ({ row, delay }) => {
  const pct = useCountUp(row.pct, { suffix: "%" });
  return (
    <motion.li initial={{ opacity: 0, x: -8 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay }}>
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0" style={{ background: `${NEON}12`, border: `1px solid ${NEON}30` }}>
          <row.icon size={14} style={{ color: NEON }} strokeWidth={1.6} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-sm font-semibold text-hx-text truncate">{row.group}</div>
            <div className="text-xs font-mono font-semibold tabular-nums" style={{ color: NEON }}>{pct}</div>
          </div>
          <div className="mt-0.5 text-[11px] text-hx-muted truncate">{row.consume}</div>
          <div className="mt-1.5 h-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.05)" }}>
            <motion.div initial={{ width: 0 }} whileInView={{ width: `${row.pct}%` }} viewport={{ once: true }} transition={{ duration: 1.4, delay: delay + 0.2, ease: [0.22, 1, 0.36, 1] }} className="h-full rounded-full" style={{ background: NEON, boxShadow: `0 0 8px ${NEON}80` }} />
          </div>
        </div>
      </div>
    </motion.li>
  );
};

function severityColor(s) {
  if (s === "grid") return CYAN;
  if (s === "wind") return CYAN;
  if (s === "solar") return SOLAR;
  return NEON;
}
function severityBg(s) { return `${severityColor(s)}14`; }
function severityBorder(s) { return `${severityColor(s)}30`; }
